import { useEffect, useState } from 'react';
import { FormContainer as HookFormContainer, SelectElement, useForm } from 'react-hook-form-mui';
import { useTranslation } from 'react-i18next';

import { Box, styled, Typography } from '@mui/material';

import { SubmitButton, translateErrorMessage } from 'Common/Form';
import { Card } from 'Components';
import { useSelectIsConfigLoading } from 'Config';
import { selectFreshStartFormDefaultValues } from 'Config/configSlice';
import type { CourseModel } from 'Courses/data';
import {
  clearOsTemplatesError,
  createFreshStartFromOsTemplate,
  createFreshStartFromStandaloneTemplate,
  getFreshStartDesktop,
  getFreshStartServersForCourse,
  getOsTemplatesOfServer,
  getStandalones,
  getTrainerDesktop,
  saveIsFreshStartMachineReadyToClone,
  useSelectAreOsTemplatesLoading,
  useSelectFreshStartServersForCourse,
  useSelectIsFreshStartCreating,
  useSelectOsTemplatesError,
  useSelectOsTemplatesOfServer,
  useSelectStandalones
} from 'Courses/data';
import { useAppDispatch, useAppSelector } from 'Store';
import { Spacing } from 'Themes/spacing';
import { Typo } from 'Themes/typo';

import type {
  ICreateFreshStartFromOsAsyncTemplateRequest,
  ICreateFreshStartFromStandaloneAsyncTemplateRequest,
  ISaveIsFreshStartMachineReadyToCloneRequest
} from '@libs/models';
import { SchoolIcon } from '@libs/shared/ui/icons';

import { Divider } from './divider';
import {
  cpuFormValues,
  diskSizeFormValues,
  type DropdownOptions,
  memoryFormValues
} from './dropdown-options.type';
import { formFieldNames } from './form-field-names';
import { OsSelectElement } from './os-select-element';

const FormContainer = styled(Box)`
  display: flex;
  gap: ${Spacing.large};
`;

const Section = styled(Box)`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 25px;
`;

const formFieldValues = {
  memory: memoryFormValues,
  cpu: cpuFormValues,
  diskSize: diskSizeFormValues
} as const;

const shouldDisplayLabelInDropdown = false;

type Props = {
  course: CourseModel;
};

export const FreshStartSettingsForm = ({ course }: Readonly<Props>) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const courseId = course.id;

  const freshStartFormDefaultValues = useAppSelector(selectFreshStartFormDefaultValues);
  const isConfigLoading = useSelectIsConfigLoading();

  const formContext = useForm<{
    serverName: string;
    memory: string;
    cpu: string;
    diskSize: string;
    template: string;
  }>({
    mode: 'all',
    defaultValues: {
      [formFieldNames.serverName]: '',
      [formFieldNames.memory]: '',
      [formFieldNames.cpu]: '',
      [formFieldNames.diskSize]: '',
      [formFieldNames.template]: ''
    }
  });

  const [serverNamesOptions, setServerNamesOptions] = useState<DropdownOptions>();
  const [osTemplateNamesOptions, setOsTemplateNamesOptions] = useState<DropdownOptions>();
  const [standalonesOptions, setStandalonesOptions] = useState<DropdownOptions>();
  const [areDropdownsDisabled, setAreDropdownsDisabled] = useState<boolean>(false);
  const [isFormInitialized, setIsFormInitialized] = useState<boolean>(false);
  const [activeServerName, setActiveServerName] = useState<string>('');

  const standalones = useSelectStandalones();
  const freshStartServers = useSelectFreshStartServersForCourse(courseId);

  const formServerName = formContext.watch(formFieldNames.serverName);
  const currentServerName =
    activeServerName ||
    (formServerName && formServerName.trim() !== ''
      ? formServerName
      : (freshStartFormDefaultValues.serverName ?? ''));
  const osTemplates = useSelectOsTemplatesOfServer(currentServerName);
  const areOsTemplatesLoading = useSelectAreOsTemplatesLoading(currentServerName);
  const hasOsTemplatesError = useSelectOsTemplatesError(currentServerName);
  const isFreshStartCreating = useSelectIsFreshStartCreating();

  const handleServerNameChange = (serverName: string) => {
    setActiveServerName(serverName);

    dispatch(clearOsTemplatesError({ serverName }));
    dispatch(getOsTemplatesOfServer({ courseId, serverName }));

    formContext.setValue(formFieldNames.template, '');
    formContext.trigger(formFieldNames.template);

    setAreDropdownsDisabled(false);
  };

  const handleTemplateChange = (template: string) => {
    const selectedStandalone = standalones?.find(x => x.id === +template);

    if (selectedStandalone) {
      setAreDropdownsDisabled(true);
      formContext.setValue(formFieldNames.serverName, selectedStandalone.serverName);
      formContext.setValue(formFieldNames.memory, selectedStandalone.memory.toString());
      formContext.setValue(formFieldNames.cpu, selectedStandalone.cpu.toString());
      formContext.setValue(formFieldNames.diskSize, selectedStandalone.diskSize.toString());
    } else {
      setAreDropdownsDisabled(false);

      const memoryId = memoryFormValues.find(
        option => freshStartFormDefaultValues.memory === option.label
      )?.id;
      formContext.setValue(formFieldNames.memory, memoryId ?? '');

      const cpuId = cpuFormValues.find(
        option => freshStartFormDefaultValues.cpu === option.label
      )?.id;
      formContext.setValue(formFieldNames.cpu, cpuId ?? '');

      const diskSizeId = diskSizeFormValues.find(
        option => freshStartFormDefaultValues.diskSize === option.label
      )?.id;
      formContext.setValue(formFieldNames.diskSize, diskSizeId ?? '');
    }
  };

  const handleSubmit = (formData: any) => {
    const onCreateFreshStartSuccess = (): void => {
      dispatch(getTrainerDesktop({ courseId }));
      dispatch(getFreshStartDesktop({ courseId }));

      dispatch(
        saveIsFreshStartMachineReadyToClone({
          courseId,
          isFreshStartMachineReadyToClone: true
        } as ISaveIsFreshStartMachineReadyToCloneRequest)
      );
    };

    const selectedStandalone = standalonesOptions?.find(x => x.id === formData.template);

    if (!selectedStandalone) {
      dispatch(
        createFreshStartFromOsTemplate({
          courseId,
          serverName: formData.serverName,
          template: formData.template,
          memory: +formData.memory,
          cpu: +formData.cpu,
          diskSize: +formData.diskSize
        } as ICreateFreshStartFromOsAsyncTemplateRequest)
      )
        .unwrap()
        .then(() => {
          onCreateFreshStartSuccess();
        });
    } else {
      dispatch(
        createFreshStartFromStandaloneTemplate({
          courseId,
          standaloneId: formData.template
        } as ICreateFreshStartFromStandaloneAsyncTemplateRequest)
      )
        .unwrap()
        .then(() => {
          onCreateFreshStartSuccess();
        });
    }
  };

  const handleTemplateClick = (id: string) => {
    formContext.setValue(formFieldNames.template, id);
    formContext.trigger(formFieldNames.template);
    handleTemplateChange(id);
  };

  const mapToDropdownOptions = (items: string[]): DropdownOptions =>
    items
      ?.map(itemName => ({ label: itemName, id: itemName }))
      .sort((a, b) => a.label.localeCompare(b.label));

  const displayLabel = (label: string): string => (shouldDisplayLabelInDropdown ? label : '');

  useEffect(() => {
    const loadInitialData = async () => {
      await Promise.all([
        dispatch(getFreshStartServersForCourse({ courseId })),
        dispatch(getStandalones())
      ]);
    };

    loadInitialData();
  }, [dispatch, courseId]);

  useEffect(() => {
    if (isConfigLoading || !freshStartFormDefaultValues.serverName || isFormInitialized) {
      return;
    }

    formContext.setValue(formFieldNames.serverName, freshStartFormDefaultValues.serverName);
    setActiveServerName(freshStartFormDefaultValues.serverName);

    const memoryId = memoryFormValues.find(
      option => freshStartFormDefaultValues.memory === option.label
    )?.id;
    if (memoryId) {
      formContext.setValue(formFieldNames.memory, memoryId);
    }

    const cpuId = cpuFormValues.find(
      option => freshStartFormDefaultValues.cpu === option.label
    )?.id;
    if (cpuId) {
      formContext.setValue(formFieldNames.cpu, cpuId);
    }

    const diskSizeId = diskSizeFormValues.find(
      option => freshStartFormDefaultValues.diskSize === option.label
    )?.id;
    if (diskSizeId) {
      formContext.setValue(formFieldNames.diskSize, diskSizeId);
    }

    dispatch(
      getOsTemplatesOfServer({
        courseId,
        serverName: freshStartFormDefaultValues.serverName
      })
    );

    setIsFormInitialized(true);
  }, [
    freshStartFormDefaultValues,
    formContext,
    dispatch,
    courseId,
    isFormInitialized,
    isConfigLoading
  ]);

  useEffect(() => {
    if (standalones && standalones.length > 0) {
      const options = standalones
        .map(standalone => ({
          label: standalone.name,
          id: standalone.id.toString()
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
      setStandalonesOptions(options);
    } else {
      setStandalonesOptions([]);
    }
  }, [standalones]);

  useEffect(() => {
    if (freshStartServers?.serverNames && Array.isArray(freshStartServers.serverNames)) {
      const options = mapToDropdownOptions(freshStartServers.serverNames);
      setServerNamesOptions(options);
    } else {
      setServerNamesOptions([]);
    }
  }, [freshStartServers]);

  useEffect(() => {
    if (osTemplates?.osTemplateNames && Array.isArray(osTemplates.osTemplateNames)) {
      const mappedOptions = mapToDropdownOptions(osTemplates.osTemplateNames);
      setOsTemplateNamesOptions(mappedOptions);
    } else {
      setOsTemplateNamesOptions([]);
    }

    const currentTemplate = formContext.getValues(formFieldNames.template);

    if (currentTemplate && !areDropdownsDisabled) {
      const templateExists = osTemplates?.osTemplateNames?.includes(currentTemplate);

      if (!templateExists) {
        formContext.setValue(formFieldNames.template, '');
        formContext.trigger(formFieldNames.template);
      }
    }
  }, [osTemplates, formContext, areDropdownsDisabled]);

  useEffect(() => {
    if (!courseId || !currentServerName || currentServerName.trim() === '' || !isFormInitialized) {
      return;
    }

    const hasDataForThisServer = osTemplates?.osTemplateNames?.length > 0;

    if (!hasDataForThisServer && !areOsTemplatesLoading && !hasOsTemplatesError) {
      dispatch(getOsTemplatesOfServer({ courseId, serverName: currentServerName }));
    }
  }, [
    currentServerName,
    courseId,
    dispatch,
    osTemplates,
    areOsTemplatesLoading,
    hasOsTemplatesError,
    isFormInitialized
  ]);

  const templatesOptions = [...(osTemplateNamesOptions ?? []), ...(standalonesOptions ?? [])];

  return (
    <HookFormContainer onSuccess={handleSubmit} formContext={formContext}>
      <Card>
        <Typography variant={Typo.title}>{t('freshStartSettingsForm.title')}</Typography>

        <FormContainer>
          <Section>
            <Typography fontWeight="bold">
              {t('freshStartSettingsForm.readyComputerTemplates')}
            </Typography>

            <OsSelectElement
              options={templatesOptions}
              onTemplateClick={handleTemplateClick}
              selectedTemplate={formContext.watch(formFieldNames.template)}
            />
          </Section>

          <Divider />

          <Section>
            <Typography fontWeight="bold">
              {t('freshStartSettingsForm.editTemplateVirtualComputer')}
            </Typography>

            <SelectElement
              label={displayLabel(t('ServerName'))}
              name={formFieldNames.serverName}
              options={serverNamesOptions ?? []}
              required
              disabled={areDropdownsDisabled}
              parseError={translateErrorMessage}
              onChange={handleServerNameChange}
            />
            <SelectElement
              label={displayLabel(t('OperationSystem'))}
              name={formFieldNames.template}
              options={templatesOptions}
              required
              parseError={translateErrorMessage}
              onChange={handleTemplateChange}
            />
            <SelectElement
              label={displayLabel(t('Memory'))}
              name={formFieldNames.memory}
              options={formFieldValues.memory}
              required
              disabled={areDropdownsDisabled}
              parseError={translateErrorMessage}
            />
            <SelectElement
              label={displayLabel(t('Processor'))}
              name={formFieldNames.cpu}
              options={formFieldValues.cpu}
              required
              disabled={areDropdownsDisabled}
              parseError={translateErrorMessage}
            />
            <SelectElement
              label={displayLabel(t('DiskSize'))}
              name={formFieldNames.diskSize}
              options={formFieldValues.diskSize}
              required
              disabled={areDropdownsDisabled}
              parseError={translateErrorMessage}
            />

            <SubmitButton
              type="submit"
              variant="contained"
              startIcon={<SchoolIcon />}
              isLoading={isFreshStartCreating}
              loadingText={t('courseForm.saving')}
              style={{ alignSelf: 'flex-end' }}
            >
              {t('freshStartSettingsForm.createComputerFreshStart')}
            </SubmitButton>
          </Section>
        </FormContainer>
      </Card>
    </HookFormContainer>
  );
};
